import { useQuery } from '@tanstack/react-query';

import { Button, ButtonLink } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';
import { trackMP } from '@/shared/mp';

import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';
import TZ_PLUS_IMAGE from './tzPlusAssets';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

const LOOKUP_KEY = 'tz-plus-week-pass';

/**
 * Where the second button sends people: Toronto Zoo's own Toronto Zoo Plus ticket page, where the
 * same week is bundled with zoo admission at a lower price and reaches us later as
 * `tz-plus-week-pass-add-on`.
 */
const ZOO_ADMISSION_URL = 'https://www.torontozoo.com/tickets/toronto-zoo-plus';

/** TODO: provisional copy, pending Toronto Zoo's sign-off. The artwork is final. */
const COPY = {
  title: 'Toronto Zoo Plus Extended Access',
  body: 'One week access for all Toronto Zoo LIVE streams and their highlights. Plus a bonus guided tour introducing the animals.',
  // Hard-coded, and deliberately so: this is Toronto Zoo's price on Toronto Zoo's site, not ours to
  // resolve. It does not follow the viewer's currency the way the price above it does, and it goes
  // stale silently if the partner reprices the add-on. Matches the CAD price on
  // `tz-plus-week-pass-add-on` in the catalogue.
  bundleHint: 'or, CA$9.99 when you buy zoo admission',
  primaryCta: 'Select Pass',
  secondaryCta: 'Discount with zoo admission',
};

interface TzPlusWeekPassProps {
  entryPoint?: string; // for analytics only
}

/**
 * The Toronto Zoo Plus week pass card.
 *
 * Partner-branded, which is why it departs from the other cards: a Toronto Zoo header rather than a
 * Zoolife one, and a "powered by zoolife" footer instead of the Zoolife voice throughout. The
 * branding is co-marketing and nothing more — Toronto Zoo's own TZ+ membership is a different
 * product that we neither sell nor recognise (see zl-api CONTEXT.md, "TZ Plus Week Pass").
 *
 * It is also the only card with two calls to action, because the same week is sold twice: here at
 * full price, and more cheaply bundled with zoo admission on the partner's site. Sending people to
 * the cheaper one mid-checkout is deliberate.
 */
const TzPlusWeekPass = ({ entryPoint }: TzPlusWeekPassProps) => {
  const { data: product } = useQuery({
    ...getProductsQueryOptions({ lookupKey: LOOKUP_KEY }),
    select: (data) => data.list[0],
  });
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  return (
    <div
      data-component="tz-plus-week-pass"
      className="flex w-full max-w-[336px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]"
    >
      {/*
        An `img`, not a background: the artwork has to be shown whole. A background with `bg-cover`
        crops to whatever box it is given, and this card's width is set by the modal around it, so
        any fixed aspect ratio would slice the partner mark off at some viewport. Letting the image
        set its own height means it always fits, at whatever width it is handed.

        `width` and `height` are the intrinsic size, so the browser reserves the right box before
        the bytes arrive and the rest of the card does not jump when they do.
      */}
      <img
        src={TZ_PLUS_IMAGE.src}
        alt={TZ_PLUS_IMAGE.alt}
        width={TZ_PLUS_IMAGE.width}
        height={TZ_PLUS_IMAGE.height}
        className="preflight block h-auto w-full"
      />

      <div className="flex w-full flex-col items-center gap-4 px-4 pt-4">
        <h4 className="preflight preflight-h4 text-center text-2xl/7 font-bold text-green-900">{COPY.title}</h4>
        <p className="preflight preflight-p text-center text-base/[18px] text-gray-500">{COPY.body}</p>
        <div className="flex flex-col items-center gap-1">
          <ProductPrice currency={product?.price.currency} price={product?.price.price} className="text-[32px]/8" />
          <p className="preflight preflight-p text-center text-sm/4 text-secondary-green">{COPY.bundleHint}</p>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3 p-4">
        <Button
          data-component="checkout-button"
          disabled={!product || isPending || eligibilityBlocked}
          loading={isPending}
          size="lg"
          className="w-full"
          onClick={() => {
            if (!product) return;
            startCheckout({
              productId: product._id,
              priceId: product.price.priceId,
              productLookupKey: product.productLookupKey,
              entryPoint,
            });
          }}
        >
          {COPY.primaryCta}
        </Button>

        {/*
          `ButtonLink`, not a `Button` with an onClick: this leaves Zoolife, so it has to be a real
          anchor — middle-click and "open in new tab" should work, and the destination should be
          visible on hover. Its own event keeps the two routes to the same week apart in the funnel.
        */}
        <ButtonLink
          data-component="zoo-admission-link"
          href={ZOO_ADMISSION_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => trackMP('click-zoo-admission-discount', { entryPoint, productLookupKey: LOOKUP_KEY })}
        >
          {COPY.secondaryCta}
        </ButtonLink>

        {product && <ProductEligibilityResult result={product.eligibility} />}

        <p className="preflight preflight-p text-center text-xs/4 text-gray-400">powered by zoolife</p>
      </div>
    </div>
  );
};

export default TzPlusWeekPass;
